import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { FileSignature, Plus, ChevronRight, BarChart3, AlertTriangle, CheckCircle, Calendar, Eye, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { API, AuthContext } from '../App';
import { useT } from '../contexts/LanguageContext';
import { useCurrency, formatCurrency, CompanyContext } from '../contexts/CompanyContext';
import RowActions from '../components/RowActions';
import { downloadEntityPdf } from '../lib/entityPdf';

const FREQUENCIES = ['daily', 'weekly', 'monthly', 'quarterly', 'half_yearly', 'annual'];

const empty = () => ({
  name: '', contract_no: '', service_provider_id: '', building_id: '',
  category: 'general', start_date: '', end_date: '',
  contract_value: 0, sla_response_hours: 24, sla_resolution_hours: 72,
  status: 'active', notes: '', scope_items: []
});

export default function AMCContracts() {
  const t = useT();
  const { user } = useContext(AuthContext);
  const { symbol } = useCurrency();
  const { company } = useContext(CompanyContext);
  const [contracts, setContracts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(empty());
  const [compliance, setCompliance] = useState(null);
  const [showCompliance, setShowCompliance] = useState(false);
  const [viewing, setViewing] = useState(null);

  const canManage = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'TECHNICIAN', 'CS_SUPERVISOR'].includes(user?.role);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, s, b] = await Promise.all([
        axios.get(`${API}/amc-contracts`, { withCredentials: true }),
        axios.get(`${API}/suppliers`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/buildings`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setContracts(c.data); setProviders(s.data); setBuildings(b.data);
    } catch { toast.error('Failed to load AMC contracts'); }
    setLoading(false);
  };

  const openNew = () => { setEditing(null); setDraft(empty()); setShowDialog(true); };
  const openEdit = (c) => {
    setEditing(c.id);
    setDraft({
      ...c,
      start_date: c.start_date?.split('T')[0] || '',
      end_date: c.end_date?.split('T')[0] || '',
      scope_items: c.scope_items || []
    });
    setShowDialog(true);
  };

  const addScopeItem = () => {
    setDraft({
      ...draft,
      scope_items: [...(draft.scope_items || []), { id: '', title: '', frequency: 'monthly', sla_target_hours: 24, description: '' }]
    });
  };
  const updateScopeItem = (idx, field, val) => {
    const next = [...(draft.scope_items || [])];
    next[idx] = { ...next[idx], [field]: val };
    setDraft({ ...draft, scope_items: next });
  };
  const removeScopeItem = (idx) => {
    setDraft({ ...draft, scope_items: draft.scope_items.filter((_, i) => i !== idx) });
  };

  const saveContract = async () => {
    if (!draft.name || !draft.service_provider_id || !draft.start_date || !draft.end_date) {
      toast.error('Name, provider, start & end dates are required'); return;
    }
    try {
      const payload = {
        ...draft,
        start_date: new Date(draft.start_date).toISOString(),
        end_date: new Date(draft.end_date).toISOString(),
        contract_value: parseFloat(draft.contract_value) || 0,
        sla_response_hours: parseFloat(draft.sla_response_hours) || 24,
        sla_resolution_hours: parseFloat(draft.sla_resolution_hours) || 72
      };
      if (editing) {
        await axios.put(`${API}/amc-contracts/${editing}`, payload, { withCredentials: true });
        toast.success('Contract updated');
      } else {
        await axios.post(`${API}/amc-contracts`, payload, { withCredentials: true });
        toast.success('AMC contract created');
      }
      setShowDialog(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const deleteContract = async (id) => {
    await axios.delete(`${API}/amc-contracts/${id}`, { withCredentials: true });
    fetchAll();
  };

  const duplicateContract = (c) => {
    const copy = {
      ...c,
      name: `${c.name} (Copy)`,
      contract_no: '',
      start_date: c.start_date ? c.start_date.split('T')[0] : '',
      end_date: c.end_date ? c.end_date.split('T')[0] : '',
    };
    delete copy.id;
    delete copy.created_at;
    setEditing(null);
    setDraft({ ...empty(), ...copy });
    setShowDialog(true);
    toast.success('Edit and save as new contract');
  };

  const downloadAmcPdf = (c) => {
    downloadEntityPdf({
      title: `AMC Contract · ${c.name}`,
      subtitle: c.contract_no || '',
      company,
      fields: [
        { label: 'Contract #', value: c.contract_no || '—' },
        { label: 'Service provider', value: providerName(c.service_provider_id) },
        { label: 'Building', value: buildingName(c.building_id) },
        { label: 'Category', value: c.category },
        { label: 'Start date', value: c.start_date?.split('T')[0] },
        { label: 'End date', value: c.end_date?.split('T')[0] },
        { label: 'Value', value: formatCurrency(c.contract_value || 0, symbol) },
        { label: 'SLA response (h)', value: c.sla_response_hours },
        { label: 'SLA resolution (h)', value: c.sla_resolution_hours },
        { label: 'Status', value: c.status },
        { label: 'Scope items', value: (c.scope_items || []).length },
      ],
      notes: c.notes,
    });
  };

  const viewCompliance = async (c) => {
    try {
      const r = await axios.get(`${API}/amc-contracts/${c.id}/compliance`, { withCredentials: true });
      setCompliance(r.data); setShowCompliance(true);
    } catch { toast.error('Failed to load compliance'); }
  };

  const providerName = (id) => providers.find(p => p.id === id)?.name || '—';
  const buildingName = (id) => buildings.find(b => b.id === id)?.name || 'Portfolio-wide';

  const daysUntilExpiry = (end) => {
    if (!end) return null;
    const d = Math.ceil((new Date(end) - new Date()) / 86400000);
    return d;
  };

  return (
    <Layout>
      <div data-testid="amc-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <FileSignature className="w-6 h-6 text-teal-600" />{t('amc.title')}
            </h1>
            <p className="text-sm text-muted-foreground">{t('amc.subtitle')}</p>
          </div>
          {canManage && (
            <Button data-testid="new-amc-btn" onClick={openNew} className="bg-teal-600 hover:bg-teal-700">
              <Plus className="w-4 h-4 mr-1" />New Contract
            </Button>
          )}
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading...</div> : (
          <div className="grid lg:grid-cols-2 gap-4">
            {contracts.length === 0 && (
              <Card className="p-12 text-center col-span-full">
                <FileSignature className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-muted-foreground">No AMC contracts yet</p>
              </Card>
            )}
            {contracts.map(c => {
              const days = daysUntilExpiry(c.end_date);
              const expiringSoon = days != null && days >= 0 && days <= 60;
              const expired = days != null && days < 0;
              return (
                <Card key={c.id} data-testid={`amc-card-${c.id}`} className="p-5 hover:shadow-lg transition">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-bold text-foreground truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {c.contract_no || 'No #'} · {providerName(c.service_provider_id)}
                      </div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                      expired ? 'bg-red-100 text-red-700' :
                      expiringSoon ? 'bg-amber-100 text-amber-700' :
                      'bg-emerald-100 text-emerald-700'
                    }`}>
                      {expired ? 'Expired' : expiringSoon ? `${days}d left` : c.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mb-3">
                    <div><Calendar className="w-3 h-3 inline mr-1" />{c.start_date?.split('T')[0]} → {c.end_date?.split('T')[0]}</div>
                    <div>Value: <strong className="text-foreground">{formatCurrency(c.contract_value || 0, symbol)}</strong></div>
                    <div>Building: {buildingName(c.building_id)}</div>
                    <div>Scope items: <strong>{(c.scope_items || []).length}</strong></div>
                    <div>SLA Response: {c.sla_response_hours}h</div>
                    <div>SLA Resolution: {c.sla_resolution_hours}h</div>
                  </div>
                  <div className="flex flex-wrap gap-1 pt-3 border-t border-slate-100">
                    <Button data-testid={`view-compliance-${c.id}`} onClick={() => viewCompliance(c)} size="sm" variant="outline">
                      <BarChart3 className="w-3.5 h-3.5 mr-1" />Compliance
                    </Button>
                    <RowActions
                      testIdPrefix={`amc-${c.id}`}
                      onView={() => setViewing(c)}
                      onEdit={canManage ? () => openEdit(c) : undefined}
                      onDuplicate={canManage ? () => duplicateContract(c) : undefined}
                      onDownloadPdf={() => downloadAmcPdf(c)}
                      onDelete={canManage ? () => deleteContract(c.id) : undefined}
                      deleteLabel={`AMC contract "${c.name}"`}
                    />
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Compliance modal */}
        <Dialog open={showCompliance} onOpenChange={setShowCompliance}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Compliance — {compliance?.contract_name}</DialogTitle></DialogHeader>
            {compliance && (
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Card className="p-3 text-center">
                    <div className="text-xs text-muted-foreground">Tasks Total</div>
                    <div className="text-2xl font-bold text-foreground">{compliance.tasks_total}</div>
                  </Card>
                  <Card className="p-3 text-center">
                    <div className="text-xs text-muted-foreground">Completed</div>
                    <div className="text-2xl font-bold text-emerald-600">{compliance.tasks_completed}</div>
                  </Card>
                  <Card className="p-3 text-center">
                    <div className="text-xs text-muted-foreground">Overdue</div>
                    <div className="text-2xl font-bold text-red-600">{compliance.tasks_overdue}</div>
                  </Card>
                  <Card className="p-3 text-center">
                    <div className="text-xs text-muted-foreground">Completion</div>
                    <div className="text-2xl font-bold text-teal-600">{compliance.completion_pct}%</div>
                  </Card>
                </div>
                {compliance.sla_hit_pct != null && (
                  <Card className="p-3">
                    <div className="text-xs text-muted-foreground">SLA Hit Rate (across {compliance.sla_eligible_reports} reports)</div>
                    <div className="text-2xl font-bold text-blue-600">{compliance.sla_hit_pct}%</div>
                  </Card>
                )}
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Per Scope Item</h3>
                  <div className="space-y-2">
                    {(compliance.per_scope || []).map((s, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg border border-slate-200">
                        <div>
                          <div className="text-sm font-medium text-slate-900">{s.title}</div>
                          <div className="text-xs text-muted-foreground">{s.frequency} · {s.tasks_completed}/{s.tasks_total} done</div>
                        </div>
                        <div className={`text-sm font-bold ${s.completion_pct >= 80 ? 'text-emerald-600' : s.completion_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                          {s.completion_pct}%
                        </div>
                      </div>
                    ))}
                    {(!compliance.per_scope || compliance.per_scope.length === 0) && (
                      <p className="text-sm text-muted-foreground text-center py-3">No scope items defined yet.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create / Edit dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Edit AMC Contract' : 'New AMC Contract'}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Contract Name *</Label><Input data-testid="amc-name-input" value={draft.name} onChange={e => setDraft({...draft, name: e.target.value})} placeholder="e.g. HVAC Annual Maintenance 2026" /></div>
                <div><Label>Contract # (optional)</Label><Input value={draft.contract_no} onChange={e => setDraft({...draft, contract_no: e.target.value})} placeholder="AMC-2026-001" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Service Provider *</Label>
                  <Select value={draft.service_provider_id} onValueChange={v => setDraft({...draft, service_provider_id: v})}>
                    <SelectTrigger data-testid="amc-provider-select"><SelectValue placeholder="Select provider" /></SelectTrigger>
                    <SelectContent>{providers.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Building (optional)</Label>
                  <Select value={draft.building_id || 'none'} onValueChange={v => setDraft({...draft, building_id: v === 'none' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Portfolio-wide" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Portfolio-wide</SelectItem>
                      {buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Start Date *</Label><Input type="date" data-testid="amc-start-input" value={draft.start_date} onChange={e => setDraft({...draft, start_date: e.target.value})} /></div>
                <div><Label>End Date *</Label><Input type="date" data-testid="amc-end-input" value={draft.end_date} onChange={e => setDraft({...draft, end_date: e.target.value})} /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>Contract Value ({symbol})</Label><Input type="number" value={draft.contract_value} onChange={e => setDraft({...draft, contract_value: e.target.value})} /></div>
                <div><Label>SLA Response (h)</Label><Input type="number" value={draft.sla_response_hours} onChange={e => setDraft({...draft, sla_response_hours: e.target.value})} /></div>
                <div><Label>SLA Resolution (h)</Label><Input type="number" value={draft.sla_resolution_hours} onChange={e => setDraft({...draft, sla_resolution_hours: e.target.value})} /></div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea rows={2} value={draft.notes || ''} onChange={e => setDraft({...draft, notes: e.target.value})} placeholder="Additional terms, exclusions, escalation contacts..." />
              </div>

              {/* Scope items editor */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-base font-semibold">AMC Scope Items</Label>
                  <Button data-testid="add-scope-btn" type="button" size="sm" variant="outline" onClick={addScopeItem}>
                    <Plus className="w-3.5 h-3.5 mr-1" />Add scope item
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mb-3">Define recurring deliverables under this AMC. Each item can be linked to PPM tasks for tracking.</p>
                {(!draft.scope_items || draft.scope_items.length === 0) && (
                  <div className="p-4 rounded-lg border-2 border-dashed border-slate-200 text-center text-sm text-muted-foreground">
                    No scope items yet. Click "Add scope item" above.
                  </div>
                )}
                {(draft.scope_items || []).map((s, idx) => (
                  <div key={idx} className="grid sm:grid-cols-12 gap-2 mb-2 p-3 rounded-lg bg-slate-50">
                    <div className="sm:col-span-5"><Input placeholder="Scope title" value={s.title} onChange={e => updateScopeItem(idx, 'title', e.target.value)} /></div>
                    <div className="sm:col-span-3">
                      <Select value={s.frequency} onValueChange={v => updateScopeItem(idx, 'frequency', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{FREQUENCIES.map(f => <SelectItem key={f} value={f}>{f.replace('_', ' ')}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-3"><Input type="number" placeholder="SLA hrs" value={s.sla_target_hours || ''} onChange={e => updateScopeItem(idx, 'sla_target_hours', parseFloat(e.target.value) || 0)} /></div>
                    <div className="sm:col-span-1 flex items-center justify-end">
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeScopeItem(idx)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                    <div className="sm:col-span-12"><Input placeholder="Brief description (optional)" value={s.description || ''} onChange={e => updateScopeItem(idx, 'description', e.target.value)} /></div>
                  </div>
                ))}
              </div>

              <Button data-testid="save-amc-btn" onClick={saveContract} className="w-full bg-teal-600 hover:bg-teal-700">
                {editing ? 'Update Contract' : 'Create Contract'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* View dialog */}
        <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{viewing?.name}</DialogTitle></DialogHeader>
            {viewing && (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="text-xs text-muted-foreground">Contract #</div><div className="font-medium">{viewing.contract_no || '—'}</div></div>
                  <div><div className="text-xs text-muted-foreground">Status</div><div className="font-medium capitalize">{viewing.status}</div></div>
                  <div><div className="text-xs text-muted-foreground">Provider</div><div className="font-medium">{providerName(viewing.service_provider_id)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Building</div><div className="font-medium">{buildingName(viewing.building_id)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Start</div><div className="font-medium">{viewing.start_date?.split('T')[0]}</div></div>
                  <div><div className="text-xs text-muted-foreground">End</div><div className="font-medium">{viewing.end_date?.split('T')[0]}</div></div>
                  <div><div className="text-xs text-muted-foreground">Value</div><div className="font-medium">{formatCurrency(viewing.contract_value || 0, symbol)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Category</div><div className="font-medium">{viewing.category}</div></div>
                  <div><div className="text-xs text-muted-foreground">SLA Response</div><div className="font-medium">{viewing.sla_response_hours}h</div></div>
                  <div><div className="text-xs text-muted-foreground">SLA Resolution</div><div className="font-medium">{viewing.sla_resolution_hours}h</div></div>
                </div>
                {viewing.notes && (<div><div className="text-xs text-muted-foreground">Notes</div><div className="text-slate-700 whitespace-pre-wrap">{viewing.notes}</div></div>)}
                {(viewing.scope_items || []).length > 0 && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Scope items ({viewing.scope_items.length})</div>
                    <ul className="list-disc pl-5 space-y-1">
                      {viewing.scope_items.map((s, i) => (
                        <li key={i}><span className="font-medium">{s.name}</span> · {s.frequency}{s.description ? ` — ${s.description}` : ''}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex gap-2 justify-end pt-2">
                  <Button variant="outline" onClick={() => downloadAmcPdf(viewing)} data-testid="amc-view-pdf-btn">Download PDF</Button>
                  <Button onClick={() => setViewing(null)}>Close</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
